import { watch, ComputedRef, inject, toRaw } from 'vue';
import { JsonFormsSubStates, toDataPath } from '@jsonforms/core';
import get from 'lodash/get';
import { useErrors } from './useErrors';
import { CommonJsonFormsControl } from './types';

/**
 * Runs one or more field-level validators declared under `uischema.options.validate` and
 * reports failures into the shared `useErrors` state (keyed by the control's own `path`).
 *
 * Configuration in uiSchema — end date must be at least 1 hour from now AND after start date:
 * ```json
 * {
 *   "type": "CustomDateControl",
 *   "label": "End date",
 *   "scope": "#/properties/changeDetails/properties/endDate",
 *   "options": {
 *     "validate": {
 *      "greaterThan": {
 *        "scope": "#/properties/changeDetails/properties/startDate",
 *        "message": "End date must be later than start date"
 *       },
 *      "minTime": {
 *        "message": "Date/time must be at least 1 hour from now",
 *        "offsetMs": 3600000
 *      },
 *      "regexp": {
 *        "message": "",
 *        "pattern": "^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)?\\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)?\\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)?\\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)(/([0-9]|[1-2][0-9]|3[0-2]))?$"
 *      }
 *     }
 *   }
 * }
 * ```
 */
export function useValidate(control: ComputedRef<CommonJsonFormsControl>) {
  const jsonforms = inject<JsonFormsSubStates>('jsonforms');

  const { addError, removeErrorsBySource } = useErrors();

  const field = control.value.path ?? 'useValidate';

  const validate = control.value.uischema?.options?.validate;
  // No `validate` block configured for this control — nothing to wire up.
  if (!validate) return;

  // "greaterThan": this control's date/time must be strictly after another field's date/time
  // (e.g. endDate > startDate). Re-checked whenever either field changes.
  if (validate.greaterThan) {
    const { scope, message } = validate.greaterThan;
    const refPath = toDataPath(scope);

    watch([() => control.value.data, () => get(jsonforms?.core?.data, refPath)], ([newValue, refValue]) => {
      if (newValue && refValue && new Date(newValue as string) <= new Date(refValue as string)) {
        // This field's value is not later than the referenced field's value — invalid.
        addError({
          message,
          source: field,
        });
      } else {
        // Either value is missing, or the ordering is valid — clear any previous error.
        removeErrorsBySource(field);
      }
    });
  }

  // "regexp": every value in this control's (array) data must match the given pattern
  // (e.g. validating a list of typed-in IP addresses).
  if (validate.regexp) {
    const { pattern, message } = validate.regexp;
    const regex = new RegExp(pattern);

    watch(
      () => control.value.data,
      (newValue) => {
        if (newValue && Array.isArray(toRaw(newValue))) {
          const findErrors = (toRaw(newValue) as unknown[]).filter((value) => !regex.test(value as string));
          if (findErrors.length > 0) {
            // At least one entry failed the pattern check — report which ones.
            addError({
              message: message || `Input IPs are not correct: ${findErrors.join(', ')}` || 'Invalid format',
              source: field,
            });
          } else {
            // Every entry matched — clear any previous error.
            removeErrorsBySource(field);
          }
        }
      },
    );
  }

  // "minTime": this control's date/time must be at least `offsetMs` milliseconds in the future
  // (e.g. changes must be scheduled at least 1 hour from now).
  if (validate.minTime) {
    const { offsetMs, message } = validate.minTime;

    watch(
      () => control.value.data,
      (newValue) => {
        if (newValue) {
          const inputTime = new Date(newValue as string).getTime();
          if (inputTime < Date.now() + offsetMs) {
            // Selected time is too soon — invalid.
            addError({
              message,
              source: field,
            });
          } else {
            // Selected time is far enough in the future — clear any previous error.
            removeErrorsBySource(field);
          }
        } else {
          // No value selected yet — nothing to validate, and no error to show.
          removeErrorsBySource(field);
        }
      },
    );
  }
}
